import React from 'react';
import { connect } from 'react-redux';
import { PLAYBACK_STATUSES } from '../../constants';
import AbstractVideoManager, { _mapDispatchToProps, _mapStateToProps } from './AbstractVideoManager';
/**
 * Manager of shared video.
 */
class VideoManager extends AbstractVideoManager {
    /**
     * Initializes a new VideoManager instance.
     *
     * @param {Object} props - This component's props.
     *
     * @returns {void}
     */
    constructor(props) {
        super(props);
        /**
         * Handles the time update event.
         *
         * @returns {void}
         */
        this.onTimeUpdate = () => {
            if (this.player) {
                this.setState({ currentTime: this.player.currentTime });
            }
            if (this.props._isOwner) {
                this.throttledFireUpdateSharedVideoEvent();
            }
        };
        /**
         * Handles metadata loaded event.
         *
         * @returns {void}
         */
        this.onLoadedMetadata = () => {
            if (this.player) {
                this.setState({
                    duration: this.player.duration,
                    volume: this.player.volume,
                    isMuted: this.player.muted,
                    isPlaying: !this.player.paused
                });
            }
        };
        /**
         * Toggles play/pause state.
         */
        this.togglePlay = () => {
            if (this.state.isPlaying) {
                this.pause();
            }
            else {
                this.play();
            }
        };
        /**
         * Toggles mute state.
         */
        this.toggleMute = () => {
            if (this.state.isMuted) {
                this.unMute();
            }
            else {
                this.mute();
            }
        };
        /**
         * Handles seek slider change.
         * @param {React.ChangeEvent<HTMLInputElement>} e - The event.
         */
        this.onSeekChange = (e) => {
            const time = Number(e.target.value);
            this.seek(time);
        };
        /**
         * Handles volume slider change.
         * @param {React.ChangeEvent<HTMLInputElement>} e - The event.
         */
        this.onVolumeSliderChange = (e) => {
            const vol = Number(e.target.value);
            if (this.player) {
                this.player.volume = vol;
                if (vol > 0 && this.player.muted) {
                    this.unMute();
                }
            }
        };
        /**
         * Toggles fullscreen.
         */
        this.toggleFullscreen = () => {
            var _a;
            const wrapper = (_a = this.playerRef.current) === null || _a === void 0 ? void 0 : _a.parentElement;
            if (wrapper) {
                if (!document.fullscreenElement) {
                    wrapper.requestFullscreen().catch(err => {
                        console.error(`Error attempting to enable fullscreen: ${err.message}`);
                    });
                }
                else {
                    document.exitFullscreen();
                }
            }
        };
        this.playerRef = React.createRef();
        this.state = {
            currentTime: 0,
            duration: 0,
            isMuted: false,
            isPlaying: false,
            showControls: true,
            volume: 1,
            isVolumeHovered: false,
            isVolumeDragging: false
        };
    }
    /**
     * Retrieves the current player ref.
     */
    get player() {
        return this.playerRef.current;
    }
    /**
     * Indicates the playback state of the video.
     *
     * @returns {string}
     */
    getPlaybackStatus() {
        let status;
        if (!this.player) {
            return;
        }
        if (this.player.paused) {
            status = PLAYBACK_STATUSES.PAUSED;
        }
        else {
            status = PLAYBACK_STATUSES.PLAYING;
        }
        return status;
    }
    /**
     * Indicates whether the video is muted.
     *
     * @returns {boolean}
     */
    isMuted() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.muted;
    }
    /**
     * Retrieves current volume.
     *
     * @returns {number}
     */
    getVolume() {
        var _a;
        return Number((_a = this.player) === null || _a === void 0 ? void 0 : _a.volume);
    }
    /**
     * Retrieves current time.
     *
     * @returns {number}
     */
    getTime() {
        var _a;
        return Number((_a = this.player) === null || _a === void 0 ? void 0 : _a.currentTime);
    }
    /**
     * Seeks video to provided time.
     *
     * @param {number} time - The time to seek to.
     *
     * @returns {void}
     */
    seek(time) {
        if (this.player) {
            this.player.currentTime = time;
            this.setState({ currentTime: time });
        }
    }
    /**
     * Plays video.
     *
     * @returns {void}
     */
    play() {
        var _a;
        const playPromise = (_a = this.player) === null || _a === void 0 ? void 0 : _a.play();
        // Handle autoplay blocking
        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                // Autoplay was blocked - this is expected when coming back from PiP
                logger.log('Video autoplay blocked - video will remain paused until user interaction:', error);
            });
        }
        return playPromise;
    }
    /**
     * Pauses video.
     *
     * @returns {void}
     */
    pause() {
        var _a;
        return (_a = this.player) === null || _a === void 0 ? void 0 : _a.pause();
    }
    /**
     * Mutes video.
     *
     * @returns {void}
     */
    mute() {
        if (this.player) {
            this.player.muted = true;
            this.setState({ isMuted: true });
        }
    }
    /**
     * Unmutes video.
     *
     * @returns {void}
     */
    unMute() {
        if (this.player) {
            this.player.muted = false;
            this.setState({ isMuted: false });
        }
    }
    /**
     * Handles volume change event.
     *
     * @returns {void}
     */
    onVolumeChange() {
        if (this.player) {
            this.setState({
                volume: this.player.volume,
                isMuted: this.player.muted
            });
        }
        super.onVolumeChange();
    }
    /**
     * Handles video play event.
     *
     * @returns {void}
     */
    onPlay() {
        this.setState({ isPlaying: true });
        super.onPlay();
    }
    /**
     * Handles video pause event.
     *
     * @returns {void}
     */
    onPause() {
        this.setState({ isPlaying: false });
        super.onPause();
    }
    /**
     * Handle video error.
     *
     * @param {Object} e - The error event.
     * @returns {void}
     */
    onError(e) {
        if (this.player && this.player.error) {
            const { code, message } = this.player.error;
            const errorData = {
                data: `Code: ${code}, Message: ${message}`
            };
            // Pass structured error to super so it logs nicely
            super.onError(errorData);
        }
        else {
            super.onError(e);
        }
    }
    /**
     * Retrieves video tag params.
     *
     * @returns {void}
     */
    getPlayerOptions() {
        const { _isOwner, videoId } = this.props;
        let options = {
            autoPlay: true,
            src: videoId,
            controls: false,
            onError: (e) => this.onError(e),
            onPlay: () => this.onPlay(),
            onPause: () => this.onPause(),
            onVolumeChange: () => this.onVolumeChange(),
            onTimeUpdate: this.onTimeUpdate,
            onLoadedMetadata: this.onLoadedMetadata
        };
        return options;
    }
    /**
     * Formats time in seconds to MM:SS or HH:MM:SS.
     * @param {number} seconds - Time in seconds.
     * @returns {string} Formatted time string.
     */
    formatTime(seconds) {
        const date = new Date(0);
        date.setSeconds(seconds);
        const timeString = date.toISOString().substr(11, 8);
        return timeString.startsWith('00:') ? timeString.substr(3) : timeString;
    }
    /**
     * Implements React Component's render.
     *
     * @inheritdoc
     */
    render() {
        const { currentTime, duration, isPlaying, volume, isMuted, showControls, isVolumeHovered, isVolumeDragging } = this.state;
        const { _isOwner } = this.props;
        return (React.createElement("div", { style: styles.videoWrapper, onMouseEnter: () => this.setState({ showControls: true }) },
            React.createElement("style", null, `
                    .video-progress::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        height: 12px;
                        width: 12px;
                        border-radius: 50%;
                        background: white;
                        cursor: pointer;
                        margin-top: -4.5px; /* Centers thumb on the track */
                        box-shadow: 0 0 2px rgba(0,0,0,0.5);
                    }
                    .video-progress::-moz-range-thumb {
                        height: 12px;
                        width: 12px;
                        border: none;
                        border-radius: 50%;
                        background: white;
                        cursor: pointer;
                        box-shadow: 0 0 2px rgba(0,0,0,0.5);
                    }
                    /* Smaller thumb for small screens/PiP */
                    @media (max-width: 500px), (max-height: 700px) {
                        .video-progress::-webkit-slider-thumb {
                            height: 8px;
                            width: 8px;
                            margin-top: -2.5px;
                        }
                        .video-progress::-moz-range-thumb {
                            height: 8px;
                            width: 8px;
                        }
                    }
                `),
            React.createElement("video", Object.assign({ id: 'sharedVideoPlayer', ref: this.playerRef }, this.getPlayerOptions(), { style: { width: '100%', height: '100%', objectFit: 'contain' } })),
            _isOwner && showControls && (React.createElement("div", { style: styles.controlsContainer },
                React.createElement("button", { onClick: this.togglePlay, style: styles.button, title: isPlaying ? "Pause" : "Play" }, isPlaying ? (React.createElement("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "white" },
                    React.createElement("path", { d: "M6 4h4v16H6V4zm8 0h4v16h-4V4z" }))) : (React.createElement("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "white" },
                    React.createElement("path", { d: "M8 5v14l11-7z" })))),
                React.createElement("div", { style: styles.volumeContainer, onMouseEnter: () => this.setState({ isVolumeHovered: true }), onMouseLeave: () => this.setState({ isVolumeHovered: false }) },
                    React.createElement("button", { onClick: this.toggleMute, style: styles.button, title: isMuted ? "Unmute" : "Mute" }, isMuted || volume === 0 ? (React.createElement("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "white" },
                        React.createElement("path", { d: "M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73 4.27 3zM12 4L9.91 6.09 12 8.18V4z" }))) : (React.createElement("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "white" },
                        React.createElement("path", { d: "M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" })))),
                    React.createElement("div", { style: {
                            width: (isVolumeHovered || isVolumeDragging) ? '60px' : '0px',
                            overflow: 'hidden',
                            transition: 'width 0.2s ease',
                            display: 'flex',
                            alignItems: 'center'
                        } },
                        React.createElement("input", { type: "range", min: 0, max: 1, step: 0.1, value: isMuted ? 0 : volume, onChange: this.onVolumeSliderChange, onMouseDown: () => this.setState({ isVolumeDragging: true }), onMouseUp: () => this.setState({ isVolumeDragging: false }), style: styles.volumeSlider, className: "volume-slider" }))),
                React.createElement("span", { style: styles.timeText },
                    this.formatTime(currentTime),
                    " / ",
                    this.formatTime(duration || 0)),
                React.createElement("input", { type: "range", min: 0, max: duration || 100, value: currentTime, onChange: this.onSeekChange, style: styles.progressBar, className: "video-progress" })))));
    }
}
const styles = {
    videoWrapper: {
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: 'black',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden'
    },
    controlsContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#4a6790', // Theme color
        padding: '0 5px', // Reduced padding
        height: '3em',
        display: 'flex',
        alignItems: 'center',
        gap: '5px', // Reduced gap for PiP/small screens
        zIndex: 2147483647, // High z-index to ensure visibility
        transition: 'all 0.4s',
        fontFamily: 'Arial, Helvetica, sans-serif'
    },
    button: {
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3em',
        height: '100%',
        outline: 'none',
    },
    volumeContainer: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
    },
    progressBar: {
        flex: 1,
        cursor: 'pointer',
        height: '3px', // Reduced height for neater look
        accentColor: 'white', // White for contrast against theme background
        margin: '0 5px', // Reduced margin
        outline: 'none',
        background: 'rgba(255,255,255,0.3)',
        borderRadius: '2px',
        appearance: 'auto',
        minWidth: 0, // Allows shrinking below content size
    },
    volumeSlider: {
        width: '60px',
        cursor: 'pointer',
        accentColor: 'white', // White for contrast against theme background
        marginLeft: '5px',
        height: '4px',
    },
    timeText: {
        color: 'white',
        fontSize: '11px',
        lineHeight: '3em',
        minWidth: 'auto',
        textAlign: 'center',
        whiteSpace: 'nowrap'
    }
};
export default connect(_mapStateToProps, _mapDispatchToProps)(VideoManager);
